import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  LifeBuoy, Paperclip, CheckCircle2, ExternalLink, Ticket, ListChecks, Activity,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authFetch } from '../services/api';

const MAX_SUBJECT = 120;
const MAX_MESSAGE = 5000;
const MAX_FILE_BYTES = 5 * 1024 * 1024;

function ExternalAction({ href, icon: Icon, title, description }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-start gap-3 rounded-xl border border-slate-700/60 bg-slate-900/50 hover:border-emerald-500/40 hover:bg-slate-900 px-3.5 py-3 transition-colors"
    >
      <div className="mt-0.5 text-emerald-400 shrink-0">
        <Icon size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-sm font-medium text-white">
          {title}
          <ExternalLink size={12} className="text-slate-500" />
        </div>
        <p className="text-slate-500 text-xs mt-0.5 leading-snug">{description}</p>
      </div>
    </a>
  );
}

export default function ContactSupport() {
  const { user } = useAuth();
  const location = useLocation();
  const [config, setConfig] = useState(null);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [ticketNumber, setTicketNumber] = useState(null);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    authFetch('/api/support/config')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data && setConfig(data))
      .catch(() => {});
  }, []);

  const supportEmail = config?.supportEmail || 'support@propscope.co.uk';
  const statusPageUrl = config?.statusPageUrl || null;
  const productName = config?.productName || 'PropScope';
  const attachmentsEnabled = config?.attachmentsEnabled !== false;
  const checkStatusUrl = config?.checkStatusUrl || 'https://support.geekybee.net/login.php';
  const myTicketsUrl = config?.myTicketsUrl || 'https://support.geekybee.net/tickets.php';
  const openTicketUrl = config?.openTicketUrl || null;

  const resetForm = () => {
    setSubject('');
    setMessage('');
    setFile(null);
    setTicketNumber(null);
    setError(null);
    setFieldErrors({});
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    const s = subject.trim();
    const m = message.trim();
    const errs = {};
    if (!s) errs.subject = 'Subject is required';
    else if (s.length > MAX_SUBJECT) errs.subject = `Max ${MAX_SUBJECT} characters`;
    if (!m) errs.message = 'Message is required';
    else if (m.length > MAX_MESSAGE) errs.message = `Max ${MAX_MESSAGE.toLocaleString()} characters`;
    if (file && file.size > MAX_FILE_BYTES) errs.attachment = 'Attachment must be 5 MB or smaller';
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      return;
    }

    setSubmitting(true);
    try {
      let res;
      const page = `${location.pathname}${location.search || ''}`.split('?')[0]
        || window.location.pathname;

      if (file && attachmentsEnabled) {
        const fd = new FormData();
        fd.append('subject', s);
        fd.append('message', m);
        fd.append('page', page);
        fd.append('attachment', file);
        res = await authFetch('/api/support', { method: 'POST', body: fd });
      } else {
        res = await authFetch('/api/support', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subject: s, message: m, page }),
        });
      }

      if (res.status === 201) {
        const data = await res.json();
        setTicketNumber(data.ticketNumber);
        return;
      }

      if (res.status === 400) {
        const data = await res.json().catch(() => ({}));
        if (data?.detail && typeof data.detail === 'object') {
          setFieldErrors(data.detail);
          return;
        }
      }

      if (res.status === 429) {
        setError('Too many support requests. Please try again later.');
        return;
      }

      setError(
        `Something went wrong sending that. Please email ${supportEmail} instead.`
      );
    } catch {
      setError(
        `Something went wrong sending that. Please email ${supportEmail} instead.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (ticketNumber) {
    return (
      <div className="max-w-xl mx-auto p-6 space-y-4">
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 space-y-3">
          <div className="flex items-center gap-2 text-emerald-400">
            <CheckCircle2 size={22} />
            <h1 className="text-xl font-semibold text-white">Thanks, we&apos;ve got it</h1>
          </div>
          <p className="text-slate-300 text-sm leading-relaxed">
            Your reference is <strong className="text-white">#{ticketNumber}</strong>.
            We&apos;ll reply to{' '}
            <span className="text-emerald-400">{user?.email}</span>.
          </p>
          <a
            href={checkStatusUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300"
          >
            View this ticket on the helpdesk
            <ExternalLink size={12} />
          </a>
          <p className="text-slate-500 text-xs">
            Use your email and ticket number <strong className="text-slate-400">#{ticketNumber}</strong> to check status or reply.
          </p>
          <button
            type="button"
            onClick={resetForm}
            className="mt-2 text-sm text-slate-400 hover:text-emerald-400"
          >
            Send another message
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto p-6 space-y-6">
      <div>
        <div className="flex items-center gap-2 text-emerald-400 mb-1">
          <LifeBuoy size={20} />
          <span className="text-xs font-medium uppercase tracking-wide">Help</span>
        </div>
        <h1 className="text-2xl font-bold text-white">Contact support</h1>
        <p className="text-slate-400 text-sm mt-1">
          Raise a new ticket here, or open the helpdesk to check status and reply to existing ones.
          We&apos;ll reply to <span className="text-slate-300">{user?.email}</span>.
        </p>
      </div>

      <div className="space-y-2">
        <ExternalAction
          href={checkStatusUrl}
          icon={Ticket}
          title="Check ticket status"
          description="Look up a ticket with your email and reference number, or sign in to reply."
        />
        <ExternalAction
          href={myTicketsUrl}
          icon={ListChecks}
          title="View my tickets"
          description="Open your helpdesk ticket list (sign in if prompted)."
        />
        {statusPageUrl && (
          <ExternalAction
            href={statusPageUrl}
            icon={Activity}
            title="System status"
            description={`See whether ${productName} is up before raising a ticket.`}
          />
        )}
      </div>

      <form onSubmit={onSubmit} className="space-y-4 rounded-2xl border border-slate-700/50 bg-slate-800/40 p-5">
        <div>
          <h2 className="text-sm font-semibold text-white">Raise a new ticket</h2>
          <p className="text-slate-500 text-xs mt-0.5">
            Handled by the {productName} team. Prefer this form over the public helpdesk.
          </p>
        </div>

        <div>
          <label htmlFor="support-subject" className="block text-xs text-slate-400 mb-1">
            Subject
          </label>
          <input
            id="support-subject"
            type="text"
            maxLength={MAX_SUBJECT}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
            placeholder="Brief summary"
          />
          {fieldErrors.subject && (
            <p className="text-rose-400 text-xs mt-1">{fieldErrors.subject}</p>
          )}
        </div>

        <div>
          <label htmlFor="support-message" className="block text-xs text-slate-400 mb-1">
            Message
          </label>
          <textarea
            id="support-message"
            rows={6}
            maxLength={MAX_MESSAGE}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm resize-y"
            placeholder="What do you need help with?"
          />
          <div className="flex justify-between mt-1">
            {fieldErrors.message ? (
              <p className="text-rose-400 text-xs">{fieldErrors.message}</p>
            ) : (
              <span />
            )}
            <p className="text-slate-600 text-xs">{message.length}/{MAX_MESSAGE}</p>
          </div>
        </div>

        {attachmentsEnabled && (
          <div>
            <label htmlFor="support-file" className="block text-xs text-slate-400 mb-1">
              Attachment <span className="text-slate-600">(optional, max 5 MB)</span>
            </label>
            <label
              htmlFor="support-file"
              className="flex items-center gap-2 cursor-pointer rounded-lg border border-dashed border-slate-600 hover:border-slate-500 px-3 py-2.5 text-sm text-slate-400"
            >
              <Paperclip size={16} />
              {file ? file.name : 'Add an image, PDF or text file'}
            </label>
            <input
              id="support-file"
              type="file"
              accept="image/*,.pdf,.txt,.csv,text/plain,text/csv,application/pdf"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            {fieldErrors.attachment && (
              <p className="text-rose-400 text-xs mt-1">{fieldErrors.attachment}</p>
            )}
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
            {error}
            {statusPageUrl && (
              <>
                {' '}
                Or check{' '}
                <a
                  href={statusPageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-white"
                >
                  System status
                </a>
                .
              </>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-white font-semibold rounded-lg py-2.5 text-sm"
        >
          {submitting ? 'Sending…' : 'Send message'}
        </button>

        {openTicketUrl && (
          <p className="text-center text-slate-500 text-xs">
            Prefer the helpdesk form?{' '}
            <a
              href={openTicketUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-emerald-400 underline"
            >
              Open on the helpdesk
            </a>
          </p>
        )}
      </form>
    </div>
  );
}
